import React, { useState, useMemo } from 'react';
import { UniversityAreaMatrixData } from '../../../types';
import { ALEXANDRIA_AREA_GROUPS } from '../../../constants/alexandriaAreas';
import { Grid, Filter, ArrowUpDown } from 'lucide-react';

interface DemandHeatmapMatrixProps {
  matrixData: UniversityAreaMatrixData;
  onCellClick?: (areaId: string, universityId: string) => void;
}

export const DemandHeatmapMatrix: React.FC<DemandHeatmapMatrixProps> = ({
  matrixData,
  onCellClick,
}) => {
  const [selectedGroup, setSelectedGroup] = useState<string>('ALL');
  const [minDemandFilter, setMinDemandFilter] = useState<number>(0);
  const [sortBy, setSortBy] = useState<'DEMAND_DESC' | 'NAME_ASC'>('DEMAND_DESC');

  // Filter & sort areas
  const filteredAreas = useMemo(() => {
    let list = matrixData.areas.filter((a) => {
      const matchGroup =
        selectedGroup === 'ALL' ||
        (selectedGroup === 'group_east' && a.areaGroupName.includes('شرق')) ||
        (selectedGroup === 'group_central' && a.areaGroupName.includes('وسط')) ||
        (selectedGroup === 'group_west' && a.areaGroupName.includes('غرب')) ||
        (selectedGroup === 'group_montaza' && a.areaGroupName.includes('المنتزه')) ||
        (selectedGroup === 'group_agami' && a.areaGroupName.includes('العجمي')) ||
        (selectedGroup === 'group_amreya' && a.areaGroupName.includes('العامرية')) ||
        (selectedGroup === 'group_borg_alarab' && a.areaGroupName.includes('برج')) ||
        (selectedGroup === 'group_gomrok' && a.areaGroupName.includes('الجمرك'));

      const matchMin = a.total >= minDemandFilter;
      return matchGroup && matchMin;
    });

    if (sortBy === 'DEMAND_DESC') {
      list = [...list].sort((x, y) => y.total - x.total);
    } else {
      list = [...list].sort((x, y) => x.areaName.localeCompare(y.areaName, 'ar'));
    }

    return list;
  }, [matrixData.areas, selectedGroup, minDemandFilter, sortBy]);

  // Universities list
  const universities = matrixData.universities;

  return (
    <div className="space-y-4">
      {/* Matrix Controls & Filters */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
        <div className="flex items-center gap-2">
          <Grid size={18} className="text-blue-600" />
          <h3 className="font-bold text-slate-800 text-sm md:text-base">
            مصفوفة تقاطع المناطق السكنية × الجامعات (Heatmap Matrix)
          </h3>
          <span className="text-xs bg-slate-100 text-slate-600 font-bold px-2 py-0.5 rounded-full">
            {filteredAreas.length} منطقة نشطة
          </span>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Group Filter */}
          <div className="flex items-center gap-1.5 text-xs">
            <Filter size={14} className="text-slate-400" />
            <select
              value={selectedGroup}
              onChange={(e) => setSelectedGroup(e.target.value)}
              className="demand-filter-select text-xs py-1.5"
            >
              <option value="ALL">جميع قطاعات الإسكندرية</option>
              {ALEXANDRIA_AREA_GROUPS.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </div>

          {/* Min Filter */}
          <select
            value={minDemandFilter}
            onChange={(e) => setMinDemandFilter(Number(e.target.value))}
            className="demand-filter-select text-xs py-1.5"
          >
            <option value={0}>جميع مستويات الطلب</option>
            <option value={2}>طالبين فأكثر</option>
            <option value={5}>5 طلاب فأكثر</option>
          </select>

          {/* Sort */}
          <button
            type="button"
            className="btn-admin-outline text-xs py-1 px-2.5 flex items-center gap-1"
            onClick={() =>
              setSortBy(sortBy === 'DEMAND_DESC' ? 'NAME_ASC' : 'DEMAND_DESC')
            }
            title="تبديل الترتيب"
          >
            <ArrowUpDown size={12} />
            <span>{sortBy === 'DEMAND_DESC' ? 'الأعلى طلباً' : 'أبجدياً'}</span>
          </button>
        </div>
      </div>

      {/* Heatmap Legend */}
      <div className="flex items-center justify-between text-xs text-slate-500 px-1 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span>تدرج كثافة الطلب:</span>
          <span className="inline-flex items-center gap-1">
            <span className="w-3.5 h-3.5 rounded bg-slate-100 border border-slate-200"></span>
            <span>0</span>
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-3.5 h-3.5 rounded bg-emerald-100 border border-emerald-200"></span>
            <span>1-3 طلاب</span>
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-3.5 h-3.5 rounded bg-emerald-300 border border-emerald-400"></span>
            <span>4-7 طلاب</span>
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-3.5 h-3.5 rounded bg-emerald-500 border border-emerald-600"></span>
            <span>8+ طلاب (تركز خط)</span>
          </span>
        </div>

        <span className="text-[11px] text-slate-400">
          * الأرقام الظاهرة داخل الخلايا تمثل عدد الطلاب الراغبين في تلك الوجهة
        </span>
      </div>

      {/* The Matrix Table */}
      <div className="matrix-container shadow-sm">
        <table className="matrix-table">
          <thead>
            <tr>
              <th className="area-col-header text-right">المنطقة السكنية \ الجامعة</th>
              {universities.map((uni) => (
                <th key={uni.universityId} title={`${uni.universityName} (${uni.governorate})`}>
                  <div className="font-bold text-slate-900">{uni.universityName}</div>
                  <div className="text-[11px] font-normal text-slate-400">
                    {uni.total} طالب إجمالي
                  </div>
                </th>
              ))}
              <th className="bg-slate-100 font-extrabold text-slate-800">إجمالي المنطقة</th>
            </tr>
          </thead>
          <tbody>
            {filteredAreas.length === 0 ? (
              <tr>
                <td colSpan={universities.length + 2} className="py-8 text-slate-400">
                  لا توجد مناطق مطابقة للفلاتر الحالية
                </td>
              </tr>
            ) : (
              filteredAreas.map((area) => (
                <tr key={area.areaId}>
                  <td className="area-col-header font-bold text-slate-800 text-right">
                    <div className="flex items-center justify-between gap-2">
                      <span>{area.areaName}</span>
                      <span className="text-[11px] font-normal text-slate-400">
                        {area.areaGroupName}
                      </span>
                    </div>
                  </td>

                  {universities.map((uni) => {
                    const key = `${area.areaId}__${uni.universityId}`;
                    const cell = matrixData.cells[key];
                    const count = cell ? cell.studentCount : 0;

                    let levelClass = 'empty';
                    if (count >= 8) levelClass = 'level-4';
                    else if (count >= 5) levelClass = 'level-3';
                    else if (count >= 2) levelClass = 'level-2';
                    else if (count >= 1) levelClass = 'level-1';

                    return (
                      <td
                        key={uni.universityId}
                        className={`matrix-cell ${levelClass}`}
                        onClick={() =>
                          count > 0 &&
                          onCellClick &&
                          onCellClick(area.areaId, uni.universityId)
                        }
                        title={
                          count > 0
                            ? `${area.areaName} ↔ ${uni.universityName}: ${count} طلاب (${cell?.attendanceDemand || 0} يوم حضور)`
                            : 'لا يوجد طلب حالياً'
                        }
                      >
                        {count > 0 ? count : '—'}
                      </td>
                    );
                  })}

                  <td className="font-mono font-extrabold bg-slate-50 text-slate-900">
                    {area.total}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
